import type { CommentTargetType } from '@cookspace-shared-contracts/core';
import { Comments } from '@cookspace-shared-contracts/core';

import { baseApi } from '@/api/baseApi';
import { parseWithSchema } from '@/lib/safeParse';

/**
 * RTK Query endpoints для admin-backend /comments/*.
 * admin-backend на порту 5002 без префикса /admin.
 *
 * GET /comments — расширенная фильтрация для модерации (search/targetType/targetId).
 * shared-contracts GetCommentsRequestSchema требует targetType+targetId, но
 * admin-backend в Phase 3 разрешает их опциональными для общего просмотра спама.
 */

interface ListCommentsArg {
    targetType?: CommentTargetType;
    targetId?: string;
    search?: string;
    limit?: number;
    offset?: number;
}

export const commentsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        listComments: build.query<Comments.GetCommentsResponse, ListCommentsArg | void>({
            query: (params) => {
                const search: Record<string, string> = {};
                if (params?.targetType) {
                    search.targetType = params.targetType;
                }
                if (params?.targetId) {
                    search.targetId = params.targetId;
                }
                if (params?.search) {
                    search.search = params.search;
                }
                if (params?.limit !== undefined) {
                    search.limit = String(params.limit);
                }
                if (params?.offset !== undefined) {
                    search.offset = String(params.offset);
                }
                return {
                    url: '/comments',
                    method: 'GET',
                    params: search
                };
            },
            transformResponse: (raw: unknown): Comments.GetCommentsResponse =>
                parseWithSchema(Comments.GetCommentsResponseSchema, raw),
            providesTags: (result) =>
                result
                    ? [
                        ...result.items.map((c) => ({
                            type: 'Comment' as const,
                            id: c.id
                        })),
                        { type: 'Comment' as const, id: 'LIST' }
                    ]
                    : [{ type: 'Comment' as const, id: 'LIST' }]
        }),

        deleteComment: build.mutation<Comments.DeleteCommentResponse, { id: string }>({
            query: ({ id }) => ({
                url: `/comments/${id}`,
                method: 'DELETE'
            }),
            transformResponse: (raw: unknown): Comments.DeleteCommentResponse =>
                parseWithSchema(Comments.DeleteCommentResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'Comment', id: arg.id },
                { type: 'Comment', id: 'LIST' }
            ]
        })
    })
});

export const { useListCommentsQuery, useDeleteCommentMutation } = commentsApi;
